import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { ErrorBoundary } from '@/app/ui/ErrorBoundary';
/**
 * Праздник загружается отдельно.
 *
 * Он тянет за собой конфетти и анимации — 45 КБ сжатого кода, которые до
 * первого выполненного дела не нужны никому, а грузились в самом первом
 * пакете, до кода любого экрана.
 */
const CelebrationOverlay = lazy(() =>
  import('@/features/celebration/ui/CelebrationOverlay').then((m) => ({
    default: m.CelebrationOverlay,
  })),
);
import { SphereCatalogProvider } from '@/entities/sphere/ui/SphereCatalogProvider';
import {
  pushIncomingBackup,
  watchIncomingBackups,
} from '@/features/data-export/model/incoming-backup';
import { useProfile } from '@/entities/profile/model/use-profile';
import { useAuthStore } from '@/features/auth/model/auth-store';
import { isNativeApp } from '@/features/data-export/model/save-backup';
import { completeOnboarding, shouldShowOnboarding } from '@/features/onboarding/model/onboarding';
import { OnboardingSheet } from '@/features/onboarding/ui/OnboardingSheet';
import { handleNotificationAction } from '@/features/reminders/model/notification-actions';
import { onNotificationAction } from '@/shared/notifications/local-notifications';
import { useGoalSettlement } from '@/features/goal-settlement/model/use-goal-settlement';
import { useDoseMaterialization } from '@/features/medication-schedule/model/use-dose-materialization';
import { useReminders } from '@/features/reminders/model/use-reminders';
import { useProfileTheme } from '@/features/theme/model/use-profile-theme';
import { useRollover } from '@/features/task-rollover/model/use-rollover';
// Точечно, а не через барель: он тянет шторку и тосты, а с ними — библиотеку
// анимаций в первый загружаемый пакет.
import { SkeletonList } from '@/shared/ui/Feedback';
import { BottomNav } from '@/widgets/navigation/BottomNav';
import { SideNav } from '@/widgets/navigation/SideNav';
import { cn } from '@/shared/lib/cn';

/**
 * Каркас основного приложения: контент + нижняя навигация.
 *
 * Ширина ограничена `max-w-lg` — на планшетах и в развёрнутом состоянии
 * складных устройств интерфейс не растягивается в неудобную «простыню», а
 * остаётся колонкой комфортной для чтения ширины.
 */
export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Напоминания заводятся в каркасе, а не на экранах: иначе напоминание о
  // таблетке приходило бы только тем, кто в тот день открывал таблетницу.
  useReminders();

  /*
    Копия, открытая в файловом менеджере, ловится в каркасе, а не на экране
    копий: приложение в этот момент только запускается, и нужного экрана ещё
    нет. Пойманная копия ждёт, а экран забирает её, когда откроется, — поэтому
    здесь только переход, без разбора файла.
  */
  useEffect(() => {
    if (!isNativeApp()) return;

    return watchIncomingBackups((text) => {
      pushIncomingBackup(text);
      void navigate('/profile');
    });
  }, [navigate]);

  /*
    Нажатие по уведомлению и его кнопки — тоже в каркасе: уведомление приходит,
    когда приложение закрыто, и открывается оно с того экрана, где его в
    прошлый раз оставили.
  */
  useEffect(
    () =>
      onNotificationAction((action) => {
        void handleNotificationAction(action, (route) => void navigate(route));
      }),
    [navigate],
  );

  /*
    Знакомство показывается в каркасе, а не на экране: человек после
    регистрации попадает в календарь, но завтра то же самое может открыться с
    любого другого места — например, по нажатию на уведомление.
  */
  const profile = useProfile();
  const user = useAuthStore((state) => state.user);

  // Состояние — только «закрыл»: показывать ли знакомство, знает сам профиль,
  // и держать это ещё и во втором месте значило бы разойтись с ним.
  const [introClosed, setIntroClosed] = useState(false);
  const introOpen = !introClosed && shouldShowOnboarding(profile, user?.email);

  const finishIntro = () => {
    setIntroClosed(true);
    if (profile) void completeOnboarding(profile);
  };

  // Перенос вчерашних задач — по той же причине: пока он жил в календаре,
  // невыполненное вчера доезжало до сегодня только у того, кто в этот заход
  // открыл календарь. У остальных задача висела в дне, но в его числа не
  // входила.
  useRollover();

  // Приёмы лекарств строятся здесь же. Планировщик напоминаний выше читает их
  // из базы и сам не создаёт: пока построение жило на вкладке «Таблетница»,
  // у того, кто туда не заглядывал, приёмы кончались через месяц — и напоминания
  // вместе с ними, хотя сам планировщик давно переехал в каркас.
  useDoseMaterialization();

  // Цель закрывается в момент достижения. Пока проверка жила на экране целей,
  // закрыть последний шаг можно было в задачах, а узнать об этом — только зайдя
  // в «Цели»: до тех пор цель считалась активной везде, а дата достижения в
  // итоге получала момент захода на экран.
  useGoalSettlement();

  // Тема тоже приходит из профиля, а он у каждого пользователя свой. Пока её
  // принимал только экран настроек, приложение сидело в чужой теме — например,
  // после входа под другим аккаунтом на том же устройстве, — и переключалось
  // само собой в момент захода в настройки.
  useProfileTheme();

  // Календарю и задачам на компьютере нужна ширина: у них две колонки.
  const wide = WIDE_SECTIONS.some((path) => location.pathname.startsWith(path));

  return (
    /*
      Каталог сфер раздаётся отсюда: свои сферы человек заводит сам, и знать о
      них должны все экраны разом — карточки задач, донат баланса, формы. Одна
      подписка на всё приложение вместо подписки в каждой карточке.
    */
    <SphereCatalogProvider>
      {/*
        Приложение одно на все экраны. Телефон — колонка шириной с экран и
        нижняя панель. Компьютер (от 1024 px) — меню слева и рабочая область
        рядом: календарь и задачи раскладываются в две колонки и берут ширину,
        остальные разделы — удобную для чтения.
      */}
      <div className="mx-auto flex min-h-dvh max-w-lg flex-col px-safe lg:max-w-none">
        <SideNav />

        <main className="flex-1 pb-nav lg:ml-[var(--side-nav-width)]">
          <div className={cn('lg:mx-auto lg:w-full', wide ? 'lg:max-w-6xl' : 'lg:max-w-3xl')}>
            {/* Граница внутри каркаса: сбой одного раздела оставляет навигацию на
              месте, и человек может уйти на работающий экран, а не перезагружать
              приложение целиком. Ключ по пути сбрасывает ошибку при переходе. */}
            <ErrorBoundary key={location.pathname} area={location.pathname}>
              <Suspense fallback={<PageFallback />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>

        <BottomNav />
        <Suspense fallback={null}>
          <CelebrationOverlay />
        </Suspense>

        {/* Знакомство поверх всего: оно приходит один раз и не мешает потом. */}
        <OnboardingSheet open={introOpen} onDone={finishIntro} />
      </div>
    </SphereCatalogProvider>
  );
}

/** Разделы, которые на компьютере раскладываются в две колонки. */
const WIDE_SECTIONS = ['/calendar', '/tasks'];

function PageFallback() {
  return (
    <div className="space-y-4 p-4 pt-8">
      <div className="h-7 w-40 animate-[var(--animate-shimmer)] rounded-md bg-surface-3" />
      <SkeletonList count={4} />
    </div>
  );
}
